/**
 * FXR — Foreign Exchange Rates reference pages:
 *   • Currency Master — ISO currency codes + translations
 *   • Currency Rates — historical rate table + manual "Fetch latest rates"
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import { useActiveLocale } from '../../lib/use_active_locale';
import type { AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { TranslationsEditor } from '../../components/common/TranslationsEditor';
import { buildTranslationsGroup } from '../../components/common/translations_group';
import type { TranslationRow } from '../../components/common/TranslationsEditor';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { hasAccess } from '../../types/auth';

const inputCls = 'mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm';
const labelCls = 'mb-1 block text-csm-sm font-medium text-csm-ink-soft';
const btnCls = 'h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt';
const primaryBtnCls = 'h-7 rounded border border-primary bg-primary px-2 text-csm-sm text-white hover:bg-primary-hover';

/* ────────────────────────── Currency Master ────────────────────────── */

const CURRENCY_PAGE = 'FXR.CURRENCY.LIST';

interface CurrencyRow {
  currency_code: string;
  symbol: string | null;
  decimal_places: number;
  sort_order: number;
  is_active: 'Y' | 'N';
  tl: { locale_code: string; display_text: string }[];
}

const CURRENCY_COLS: GridColumnDef[] = [
  { key: 'currency_code', header: 'Code', defaultWidth: 100 },
  { key: 'symbol', header: 'Symbol', defaultWidth: 80 },
  { key: 'decimal_places', header: 'Decimals', defaultWidth: 100 },
  { key: 'display_text', header: 'Name', defaultWidth: 280 },
  { key: 'is_active', header: 'Active', defaultWidth: 80 },
];

function CurrencyMasterInner({ session }: { session: AuthSession }) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, CURRENCY_PAGE, 'add');
  const canChange = hasAccess(session, CURRENCY_PAGE, 'change');

  const { t } = useTranslation();
  const locale = useActiveLocale();
  const [rows, setRows] = useState<CurrencyRow[]>([]);
  const [filter, setFilter] = useState('');
  // §17.B12: Active facet defaults to Active-only on first visit.
  const [activeFacet, setActiveFacet] = useState<string[]>(['Y']);
  const [editing, setEditing] = useState<CurrencyRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb.from('fxr_currency').select(`
        currency_code, symbol, decimal_places, sort_order, is_active,
        fxr_currency_tl ( locale_code, display_text )
      `).order('sort_order');
      if (e) throw e;
      // The gateway returns the embedded translations under the RELATION name,
      // so they have to be mapped onto `tl` — a blanket cast left `tl` undefined
      // and every render of the name column threw.
      type RawCurrency = Omit<CurrencyRow, 'tl'> & { fxr_currency_tl?: CurrencyRow['tl'] };
      setRows(((data ?? []) as unknown as RawCurrency[]).map(r => ({
        currency_code: r.currency_code,
        symbol: r.symbol,
        decimal_places: r.decimal_places,
        sort_order: r.sort_order,
        is_active: r.is_active,
        tl: r.fxr_currency_tl ?? [],
      })));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const tlName = (tl: { locale_code: string; display_text: string }[], loc: string) =>
    tl.find(x => x.locale_code === loc)?.display_text ?? tl.find(x => x.locale_code === 'EN_US')?.display_text ?? '';

  // §17.B13: related-children expand/contract (keyed by currency_code). Each currency
  // already carries its per-locale translations — surface them as a child group.
  const detail = useExpandedRows<string>();
  const buildRelatedGroups = (code: string): RelatedChildGroup[] => {
    const row = rows.find(r => r.currency_code === code);
    if (!row) return [];
    return [
      buildTranslationsGroup(row.tl, t, locale),
    ];
  };
  const hasRelated = (code: string) => buildRelatedGroups(code).some(g => g.items.length > 0);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter(r =>
      (!q || r.currency_code.toLowerCase().includes(q) || tlName(r.tl, locale).toLowerCase().includes(q)) &&
      (activeFacet.length === 0 || activeFacet.includes(r.is_active))
    );
  }, [rows, filter, activeFacet, locale]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="text-csm-sm font-semibold uppercase tracking-wider text-csm-ink-soft">{t('fxr.currency.title', 'Currencies')}</span>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={t('common.filter', 'Filter…')}
          className="h-7 w-44 rounded border border-csm-border px-2 text-csm-sm focus:border-csm-watermark focus:outline-none"
        />
        <FacetFilter
          label={t('common.boolean.active', 'Active')}
          options={[
            { value: 'Y', label: t('common.boolean.yes', 'Yes') },
            { value: 'N', label: t('common.boolean.no', 'No') },
          ]}
          selected={activeFacet}
          onChange={setActiveFacet}
        />
        <ClearFiltersButton
          active={filter.trim() !== '' || activeFacet.length > 0}
          onClear={() => {
            setFilter('');
            setActiveFacet([]);
          }}
        />
        {canAdd && (<button onClick={() => setCreating(true)} className={primaryBtnCls}>
          + {t('common.new', 'New')}
        </button>)}
        <button onClick={() => void reload()} className={btnCls}>
          {t('common.refresh', 'Refresh')}
        </button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('common.loading', 'Loading…')}</span>}
        {error && <span className="text-csm-xs text-csm-danger">{error}</span>}
        <div className="ml-auto">
          <PersonalizationToggle />
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={CURRENCY_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('common.related_lbl', 'Related records')} />
                  {visibleCols.map(col => (
                    <th
                      key={col.key}
                      className="border-b border-csm-border px-3 py-1.5 text-left"
                      style={widths[col.key] ? { width: widths[col.key] } : undefined}
                    >
                      {col.header}
                    </th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>
                    {t('common.actions', 'Actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.currency_code}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(r.currency_code)}
                        hasChildren={hasRelated(r.currency_code)}
                        onToggle={() => detail.toggle(r.currency_code)}
                        expandLabel={t('common.related_expand', 'Show related records')}
                        collapseLabel={t('common.related_collapse', 'Hide related')}
                        tooltip={t('common.related_lbl', 'Related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td
                        key={col.key}
                        className="border-b border-csm-border-soft px-3 py-1.5"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}
                      >
                        {col.key === 'currency_code' && <span className="font-mono text-csm-sm">{r.currency_code}</span>} {/* fk-display-ok: ISO currency code */}
                        {col.key === 'symbol' && r.symbol}
                        {col.key === 'decimal_places' && r.decimal_places}
                        {col.key === 'display_text' && tlName(r.tl, locale)}
                        {col.key === 'is_active' && (r.is_active === 'Y' ? t('common.boolean.yes', 'Yes') : t('common.boolean.no', 'No'))}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                      {canChange && (<button onClick={() => setEditing(r)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">
                        {t('common.edit', 'Edit')}
                      </button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.currency_code) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.currency_code)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr>
                    <td colSpan={visibleCols.length + 2} className="py-12 text-center">
                      <EmptyState title={t('common.no_records', 'No records')} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
      </div>

      {(editing || creating) && (
        <CurrencyModal
          session={session}
          row={editing}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            void reload();
          }}
        />
      )}
    </div>
  );
}

function CurrencyModal({
  session,
  row,
  onClose,
  onSaved,
}: {
  session: AuthSession;
  row: CurrencyRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, CURRENCY_PAGE, 'change');

  const { t } = useTranslation();
  const isCreate = row === null;
  const [code, setCode] = useState(row?.currency_code ?? '');
  const [symbol, setSymbol] = useState(row?.symbol ?? '');
  const [decimals, setDecimals] = useState(String(row?.decimal_places ?? 2));
  const [sortOrder, setSortOrder] = useState(String(row?.sort_order ?? 0));
  const [isActive, setIsActive] = useState(!row || row.is_active === 'Y');
  const [translations, setTranslations] = useState<TranslationRow[]>(row?.tl ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!code.trim()) throw new Error(t('common.err.code_required', 'Code is required.'));
      if (!translations.some(t => t.display_text?.trim())) throw new Error(t('common.err.translation_required', 'At least one translation is required.'));

      const currencyCode = isCreate ? code.trim().toUpperCase() : row.currency_code;

      if (isCreate) {
        const { error: e } = await routedInsert(session, 'fxr_currency', {
          currency_code: currencyCode,
          symbol: symbol.trim() || null,
          decimal_places: Number(decimals),
          sort_order: Number(sortOrder),
          is_active: isActive ? 'Y' : 'N',
        });
        if (e) throw new Error(e.message);
      } else {
        const { error: e } = await routedUpdate(session, 'fxr_currency', 'currency_code', row.currency_code, {
          symbol: symbol.trim() || null,
          decimal_places: Number(decimals),
          sort_order: Number(sortOrder),
          is_active: isActive ? 'Y' : 'N',
        });
        if (e) throw new Error(e.message);
      }

      // §8/§2: _tl companions have composite PKs (currency_code, locale_code) the
      // Common Routine cannot address — they are the sanctioned direct-write
      // exception (see scripts/check_write_path.mjs). Upsert each translation so
      // BOTH create and edit persist; a routedInsert here was rejected as an
      // unregistered table and the Promise.all swallowed the error, so no
      // translation was ever written (blank name on new currencies).
      for (const tr of translations.filter(x => x.display_text?.trim())) {
        const { error: te } = await apiDb.from('fxr_currency_tl').upsert({
          currency_code: currencyCode,
          locale_code: tr.locale_code,
          display_text: tr.display_text,
        }, { onConflict: 'currency_code,locale_code' });
        if (te) throw new Error(te.message);
      }
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isCreate ? t('fxr.currency.new', 'New Currency') : `${t('common.edit', 'Edit')} — ${row?.currency_code}`}
      onClose={onClose}
      width="md"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">
            {t('common.cancel', 'Cancel')}
          </button>
          {canChange && (<button
            onClick={() => void save()}
            disabled={busy}
            className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60"
          >
            {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
          </button>)}
        </>
      }
    >
      <PersonalizedField componentKey="modal" fieldKey="currency_code" label={t('common.code', 'Code')}>
        <label className={labelCls}>{t('common.code', 'Code')}</label>
        <input
          type="text"
          value={code}
          onChange={e => setCode(e.target.value)}
          disabled={!isCreate}
          placeholder="USD"
          className={inputCls}
        />
      </PersonalizedField>

      <PersonalizedField componentKey="modal" fieldKey="symbol" label={t('fxr.currency.symbol', 'Symbol')}>
        <label className={labelCls}>{t('fxr.currency.symbol', 'Symbol')}</label>
        <input type="text" value={symbol} onChange={e => setSymbol(e.target.value)} placeholder="$" className={inputCls} />
      </PersonalizedField>

      <PersonalizedField componentKey="modal" fieldKey="decimal_places" label={t('fxr.currency.decimals', 'Decimal Places')}>
        <label className={labelCls}>{t('fxr.currency.decimals', 'Decimal Places')}</label>
        <input type="number" value={decimals} onChange={e => setDecimals(e.target.value)} className={inputCls} />
      </PersonalizedField>

      <PersonalizedField componentKey="modal" fieldKey="sort_order" label={t('common.sort_order', 'Sort Order')}>
        <label className={labelCls}>{t('common.sort_order', 'Sort Order')}</label>
        <input type="number" value={sortOrder} onChange={e => setSortOrder(e.target.value)} className={inputCls} />
      </PersonalizedField>

      <PersonalizedField componentKey="modal" fieldKey="is_active" label={t('common.boolean.active', 'Active')}>
        <label className="mb-3 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
          <input
            type="checkbox"
            checked={isActive}
            onChange={e => setIsActive(e.target.checked)}
            className="h-4 w-4 accent-gray-700"
          />
          {t('common.boolean.active', 'Active')}
        </label>
      </PersonalizedField>

      <TranslationsEditor
        value={translations}
        onChange={setTranslations}
        label={t('common.translations_lbl', 'Translations')}
      />

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

export function FxrCurrencyPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={CURRENCY_PAGE} session={session}>
      <CurrencyMasterInner session={session} />
    </PersonalizationProvider>
  );
}

/* ────────────────────────── Currency Rates ────────────────────────── */

const RATE_PAGE = 'FXR.RATE.LIST';

interface RateRow {
  fxr_rate_id: number;
  base_currency_code: string;
  quote_currency_code: string;
  rate: number;
  effective_at: string;
  rate_source_code: string;
}

const RATE_COLS: GridColumnDef[] = [
  { key: 'base_currency_code', header: 'From', defaultWidth: 80 },
  { key: 'quote_currency_code', header: 'To', defaultWidth: 80 },
  { key: 'rate', header: 'Rate', defaultWidth: 120 },
  { key: 'effective_at', header: 'Effective At', defaultWidth: 190 },
  { key: 'rate_source_code', header: 'Source', defaultWidth: 100 },
];

function CurrencyRateInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<RateRow[]>([]);
  const [currencyFacet, setCurrencyFacet] = useState<string[]>([]);
  const [sourceFacet, setSourceFacet] = useState<string[]>([]);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('fxr_rate')
        .select('fxr_rate_id, base_currency_code, quote_currency_code, rate, effective_at, rate_source_code')
        .order('effective_at', { ascending: false })
        .limit(500);
      if (e) throw e;
      setRows((data ?? []) as unknown as RateRow[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const refreshNow = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb.rpc('fxr_refresh');
      if (e) throw e;
      const res = data as { ok?: boolean; error?: string } | null;
      if (res && res.ok === false) throw new Error(res.error ?? 'FX refresh failed');
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRefreshing(false);
    }
  };

  const currencies = useMemo(
    () =>
      Array.from(
        new Set([
          ...rows.map(r => r.base_currency_code),
          ...rows.map(r => r.quote_currency_code),
        ])
      ).sort(),
    [rows]
  );
  const sources = useMemo(() => Array.from(new Set(rows.map(r => r.rate_source_code))).sort(), [rows]);

  // §17.B13: related-children expand/contract (keyed by fxr_rate_id). A rate is a flat
  // historical row with no localized child collections loaded here — the control renders
  // inert. TODO: wire relations (e.g. resolved currency names) if the fetch is extended.
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [];
  const hasRelated = (_id: number) => false;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter(
      r =>
        (!q ||
          r.base_currency_code.toLowerCase().includes(q) ||
          r.quote_currency_code.toLowerCase().includes(q) ||
          r.rate_source_code.toLowerCase().includes(q)) &&
        (currencyFacet.length === 0 || currencyFacet.includes(r.base_currency_code) || currencyFacet.includes(r.quote_currency_code)) &&
        (sourceFacet.length === 0 || sourceFacet.includes(r.rate_source_code))
    );
  }, [rows, filter, currencyFacet, sourceFacet]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="text-csm-sm font-semibold uppercase tracking-wider text-csm-ink-soft">{t('fxr.rate.title', 'Exchange Rates')}</span>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={t('common.filter', 'Filter…')}
          className="h-7 w-44 rounded border border-csm-border px-2 text-csm-sm focus:border-csm-watermark focus:outline-none"
        />
        <FacetFilter
          label={t('fxr.rate.facet.currency', 'Currency')}
          options={currencies.map(c => ({ value: c, label: c }))}
          selected={currencyFacet}
          onChange={setCurrencyFacet}
        />
        <FacetFilter
          label={t('fxr.rate.facet.source', 'Source')}
          options={sources.map(s => ({ value: s, label: s }))}
          selected={sourceFacet}
          onChange={setSourceFacet}
        />
        <ClearFiltersButton
          active={filter.trim() !== '' || currencyFacet.length > 0 || sourceFacet.length > 0}
          onClear={() => {
            setFilter('');
            setCurrencyFacet([]);
            setSourceFacet([]);
          }}
        />
        <button onClick={() => void refreshNow()} disabled={refreshing} className={primaryBtnCls}>
          {refreshing ? t('fxr.rate.refreshing', 'Fetching…') : t('fxr.rate.refresh_now', 'Fetch latest rates')}
        </button>
        <button onClick={() => void reload()} className={btnCls}>
          {t('common.refresh', 'Refresh')}
        </button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('common.loading', 'Loading…')}</span>}
        {error && <span className="text-csm-xs text-csm-danger">{error}</span>}
        <div className="ml-auto">
          <PersonalizationToggle />
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={RATE_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('common.related_lbl', 'Related records')} />
                  {visibleCols.map(col => (
                    <th
                      key={col.key}
                      className="border-b border-csm-border px-3 py-1.5 text-left"
                      style={widths[col.key] ? { width: widths[col.key] } : undefined}
                    >
                      {col.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.fxr_rate_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(r.fxr_rate_id)}
                        hasChildren={hasRelated(r.fxr_rate_id)}
                        onToggle={() => detail.toggle(r.fxr_rate_id)}
                        expandLabel={t('common.related_expand', 'Show related records')}
                        collapseLabel={t('common.related_collapse', 'Hide related')}
                        tooltip={t('common.related_lbl', 'Related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td
                        key={col.key}
                        className="border-b border-csm-border-soft px-3 py-1.5"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}
                      >
                        {col.key === 'base_currency_code' && <span className="font-mono text-csm-sm">{r.base_currency_code}</span>} {/* fk-display-ok: ISO currency code */}
                        {col.key === 'quote_currency_code' && <span className="font-mono text-csm-sm">{r.quote_currency_code}</span>} {/* fk-display-ok: ISO currency code */}
                        {col.key === 'rate' && r.rate.toFixed(6)}
                        {col.key === 'effective_at' && new Date(r.effective_at).toLocaleString()}
                        {col.key === 'rate_source_code' && r.rate_source_code} {/* fk-display-ok: source code is self-explanatory */}
                      </td>
                    ))}
                  </tr>
                  {detail.isOpen(r.fxr_rate_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 1} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.fxr_rate_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr>
                    <td colSpan={visibleCols.length + 1} className="py-12 text-center">
                      <EmptyState title={t('common.no_records', 'No records')} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
      </div>
    </div>
  );
}

export function FxrCurrencyRatePage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={RATE_PAGE} session={session}>
      <CurrencyRateInner session={session} />
    </PersonalizationProvider>
  );
}
